import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { asArray } from "@/lib/arrays";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Trophy,
  Clock,
  CheckCircle,
  XCircle,
  Play,
  Target,
  Award,
  Timer
} from "lucide-react";
import { toast } from "sonner";

const QuizPage = () => {
  const { user } = useAuth();
  const [quizzes, setQuizzes] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(0);
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let timer;
    if (quizStarted && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [quizStarted, timeLeft]);

  // Auto-submit when time runs out
  useEffect(() => {
    if (quizStarted && timeLeft === 0 && selectedQuiz) {
      submitQuiz();
    }
  }, [timeLeft]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [quizzesRes, attemptsRes] = await Promise.all([
        axios.get(`${API}/quizzes`, { withCredentials: true }),
        axios.get(`${API}/quizzes/attempts/my`, { withCredentials: true })
      ]);
      setQuizzes(asArray(quizzesRes.data, "quizzes"));
      setAttempts(asArray(attemptsRes.data, "attempts"));
    } catch (error) {
      console.error("Error fetching data:", error); // eslint-disable-line no-console
    } finally {
      setLoading(false);
    }
  };

  const startQuiz = (quiz) => {
    setSelectedQuiz(quiz);
    setCurrentQuestion(0);
    setAnswers([]);
    setTimeLeft(quiz.time_limit_minutes * 60);
    setQuizStarted(true);
    setQuizResult(null);
  };

  const selectAnswer = (answer) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = { question_index: currentQuestion, answer };
    setAnswers(newAnswers);
  };

  const nextQuestion = () => {
    if (currentQuestion < (selectedQuiz.questions || []).length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const submitQuiz = useCallback(async () => {
    if (!selectedQuiz) return;
    
    try {
      const response = await axios.post(
        `${API}/quizzes/submit`,
        {
          quiz_id: selectedQuiz.quiz_id,
          answers: answers,
          time_taken_seconds: (selectedQuiz.time_limit_minutes * 60) - timeLeft
        },
        { withCredentials: true }
      );
      setQuizResult(response.data);
      setQuizStarted(false);
      fetchData();
      
      if (response.data.passed) {
        toast.success("Congratulations! You passed the quiz!");
      } else {
        toast.error("Quiz completed. Keep practicing!");
      }
    } catch (error) {
      toast.error("Failed to submit quiz");
    }
  }, [selectedQuiz, answers, timeLeft]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getAttemptForQuiz = (quizId) => {
    return attempts.find((a) => a.quiz_id === quizId);
  };

  if (loading) {
    return (
      <DashboardLayout title="Quizzes">
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#00d991] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Quizzes & Competitions">
      <div className="space-y-6" data-testid="quiz-page">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white dark:bg-[#1a1f2e] border-gray-200 dark:border-[#2a3142]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#1e88e5]/20 flex items-center justify-center">
                  <Target className="w-5 h-5 text-[#1e88e5]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white font-outfit">{quizzes.length}</p>
                  <p className="text-xs text-[#6b7280]">Available Quizzes</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-[#1a1f2e] border-gray-200 dark:border-[#2a3142]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#00d991]/20 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-[#00d991]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white font-outfit">{attempts.length}</p>
                  <p className="text-xs text-[#6b7280]">Attempts</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-[#1a1f2e] border-gray-200 dark:border-[#2a3142]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#4caf50]/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-[#4caf50]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white font-outfit">
                    {attempts.filter((a) => a.passed).length}
                  </p>
                  <p className="text-xs text-[#6b7280]">Passed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-[#1a1f2e] border-gray-200 dark:border-[#2a3142]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#ff9800]/20 flex items-center justify-center">
                  <Award className="w-5 h-5 text-[#ff9800]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white font-outfit">
                    {attempts.length > 0 
                      ? Math.round(attempts.reduce((acc, a) => acc + a.score, 0) / attempts.length)
                      : 0}%
                  </p>
                  <p className="text-xs text-[#6b7280]">Avg Score</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quiz List */}
        <Card className="bg-white dark:bg-[#1a1f2e] border-gray-200 dark:border-[#2a3142]" data-testid="quiz-list">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-white font-outfit flex items-center gap-2">
              <Trophy className="w-5 h-5 text-[#00d991]" strokeWidth={1.5} />
              Available Quizzes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {quizzes.length === 0 ? (
              <div className="text-center py-8">
                <Trophy className="w-12 h-12 text-[#6b7280] mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-[#6b7280]">No quizzes available yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {quizzes.map((quiz) => {
                  const attempt = getAttemptForQuiz(quiz.quiz_id);
                  return (
                    <div
                      key={quiz.quiz_id}
                      className="bg-white dark:bg-[#0f1419] border border-gray-200 dark:border-[#2a3142] rounded-lg p-5"
                      data-testid={`quiz-card-${quiz.quiz_id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-semibold text-gray-900 dark:text-white">{quiz.title}</h3>
                        {attempt && (
                          <Badge className={attempt.passed 
                            ? "bg-[#4caf50]/20 text-[#4caf50]" 
                            : "bg-[#f44336]/20 text-[#f44336]"
                          }>
                            {attempt.passed ? "Passed" : "Failed"}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-[#6b7280] mb-4">{quiz.description}</p>
                      <div className="flex items-center gap-4 text-xs text-[#6b7280] mb-4">
                        <span className="flex items-center gap-1">
                          <Target className="w-3 h-3" />
                          {quiz.questions?.length || 0} Questions
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {quiz.time_limit_minutes} mins
                        </span>
                        <span className="flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Pass: {quiz.passing_score}%
                        </span>
                      </div>
                      {attempt && (
                        <div className="mb-4">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-[#6b7280]">Your Score</span>
                            <span className="text-[#00d991] font-mono">{Math.round(attempt.score)}%</span>
                          </div>
                          <Progress value={attempt.score} className="h-2 bg-white dark:bg-[#2a3142]" />
                        </div>
                      )}
                      <Button
                        onClick={() => startQuiz(quiz)}
                        className="w-full bg-[#00d991] text-black hover:bg-[#00ff9f]"
                        data-testid={`start-quiz-${quiz.quiz_id}`}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        {attempt ? "Retry Quiz" : "Start Quiz"}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quiz Dialog */}
        <Dialog open={quizStarted || !!quizResult} onOpenChange={() => {
          setQuizStarted(false);
          setQuizResult(null);
          setSelectedQuiz(null);
        }}>
          <DialogContent className="bg-white dark:bg-[#1a1f2e] border-gray-200 dark:border-[#2a3142] text-gray-900 dark:text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-outfit flex items-center justify-between">
                <span>{selectedQuiz?.title}</span>
                {quizStarted && (
                  <Badge className={timeLeft < 60 ? "bg-[#f44336]/20 text-[#f44336]" : "bg-[#00d991]/20 text-[#00d991]"}>
                    <Timer className="w-3 h-3 mr-1" />
                    {formatTime(timeLeft)}
                  </Badge>
                )}
              </DialogTitle>
            </DialogHeader>

            {quizResult ? (
              <div className="text-center py-8">
                <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${
                  quizResult.passed ? "bg-[#4caf50]/20" : "bg-[#f44336]/20"
                }`}>
                  {quizResult.passed ? (
                    <CheckCircle className="w-10 h-10 text-[#4caf50]" />
                  ) : (
                    <XCircle className="w-10 h-10 text-[#f44336]" />
                  )}
                </div>
                <h3 className="text-2xl font-bold mb-2 font-outfit">
                  {quizResult.passed ? "Congratulations!" : "Keep Practicing!"}
                </h3>
                <p className="text-[#6b7280] mb-4">
                  You scored {quizResult.correct} out of {quizResult.total} questions
                </p>
                <div className="text-4xl font-bold text-[#00d991] font-outfit mb-6">
                  {Math.round(quizResult.score)}%
                </div>
                <Button
                  onClick={() => {
                    setQuizResult(null);
                    setSelectedQuiz(null);
                  }}
                  className="bg-[#00d991] text-black hover:bg-[#00ff9f]"
                >
                  Close
                </Button>
              </div>
            ) : quizStarted && selectedQuiz && (
              <div className="space-y-6">
                <Progress 
                  value={(currentQuestion + 1) / (selectedQuiz.questions || []).length * 100} 
                  className="h-2 bg-white dark:bg-[#2a3142]"
                />
                <p className="text-sm text-[#6b7280]">
                  Question {currentQuestion + 1} of {(selectedQuiz.questions || []).length}
                </p>
                
                <div className="bg-white dark:bg-[#0f1419] rounded-lg p-6">
                  <h4 className="text-lg font-medium mb-4">
                    {selectedQuiz.questions[currentQuestion]?.question}
                  </h4>
                  {selectedQuiz.questions[currentQuestion]?.image_url && (
                    <div className="mb-6 flex justify-center bg-black/30 rounded-lg p-2 border border-gray-200 dark:border-[#2a3142]">
                      <img 
                        src={selectedQuiz.questions[currentQuestion].image_url.startsWith('http') ? selectedQuiz.questions[currentQuestion].image_url : API.replace('/api', '') + selectedQuiz.questions[currentQuestion].image_url} 
                        alt="Question Diagram" 
                        className="max-h-64 object-contain rounded"
                      />
                    </div>
                  )}
                  <div className="space-y-3">
                    {selectedQuiz.questions[currentQuestion]?.options?.map((option, idx) => (
                      <button
                        key={idx}
                        onClick={() => selectAnswer(option)}
                        className={`w-full text-left p-4 rounded-lg border transition-all ${
                          answers[currentQuestion]?.answer === option
                            ? "border-[#00d991] bg-[#00d991]/10"
                            : "border-[#2a3142] hover:border-[#3d4559]"
                        }`}
                        data-testid={`option-${idx}`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button
                    variant="outline"
                    onClick={prevQuestion}
                    disabled={currentQuestion === 0}
                    className="border-gray-200 dark:border-[#2a3142] text-[#b0b8c4] hover:bg-[#252b3d]"
                  >
                    Previous
                  </Button>
                  {currentQuestion === (selectedQuiz.questions || []).length - 1 ? (
                    <Button
                      onClick={submitQuiz}
                      className="bg-[#00d991] text-black hover:bg-[#00ff9f]"
                      data-testid="submit-quiz-btn"
                    >
                      Submit Quiz
                    </Button>
                  ) : (
                    <Button
                      onClick={nextQuestion}
                      className="bg-[#00d991] text-black hover:bg-[#00ff9f]"
                    >
                      Next
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default QuizPage;
